import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import {
  useCircuitDiscoveries,
  type UseCircuitDiscoveriesResult,
} from '../hooks/useCircuitDiscoveries';
type CircuitDiscoveryContextValue = UseCircuitDiscoveriesResult & {
  resumeForCurrentRoute: () => void;
};

const CircuitDiscoveryContext = createContext<CircuitDiscoveryContextValue | null>(null);

export function CircuitDiscoveryProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const sessionRoute = location.pathname.startsWith('/sessions');
  const [resumedSessionPath, setResumedSessionPath] = useState<string | null>(null);
  const discovery = useCircuitDiscoveries(
    !sessionRoute || resumedSessionPath === location.pathname,
  );
  const resumeForCurrentRoute = useCallback(() => {
    setResumedSessionPath(location.pathname);
  }, [location.pathname]);
  const value = useMemo(
    () => ({ ...discovery, resumeForCurrentRoute }),
    [discovery, resumeForCurrentRoute],
  );

  return <CircuitDiscoveryContext.Provider value={value}>{children}</CircuitDiscoveryContext.Provider>;
}

export function useCircuitDiscovery() {
  const context = useContext(CircuitDiscoveryContext);
  if (!context) {
    throw new Error('useCircuitDiscovery must be used within CircuitDiscoveryProvider');
  }
  return context;
}
